import React, { useEffect, useState, useContext } from 'react';

import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

import BenefitGrid from './benefitGrid';
import BenefitModal from './benefitModal';

import { VIEW, UPDATE, DELETE, NEW } from '../../assets/Constants/actionsTypes';
import Notifications, { ERROR_NOTIFICATION, SUCCESS_NOTIFICATION } from '../../helpers/notifications';

import { useTranslation } from 'react-i18next';

import API from "../../services/api";

import AuthContext from '../../context/Auth/AuthContext';

import { validate_benefit, clean_benefit } from './benefit_cleanAndValidate';

import './benefit.css';

const Benefit = () => {
    // * we call the hook to make translations
    const { t } = useTranslation('common');
    // Modal Window type = DELETE, NEW, VIEW or UPDATE
    const [viewType, setViewType] = useState('');
    // Selected benefit
    const [benefitSelected, setBenefitSelected] = useState({});
    /**State of the modal window. True = showing ... False = hidden */
    const [showModal, setShowModal] = useState(false);
    /** State of the dialog. True = showing ... False = hidden */
    const [showAlert, setShowAlert] = useState(false);
    // List of groups. See useEffect
    const [benefitList, setBenefitList] = useState([]);

    // List of spFunctions. See useEffect
    const [spFunctionList, setSpFunctionList] = useState([]);

    // List of spComponents. See useEffect
    const [spComponentList, setSpComponentList] = useState([]);

    // List of spGuarantee. See useEffect
    const [spGuaranteeList, setSpGuaranteeList] = useState([]);

    // Session context to get the current institution data
    const { get_currentInstitution } = useContext(AuthContext);


    const getBenefits = async () => {
        setBenefitList([]);
        try {
            // * call the API
            const res = await API.get(`/benefit/getAll?institutionId=${get_currentInstitution().catInstitutionId}`);
            setBenefitList(res.data.data);
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e);
        }
    }

    const getAllComboList = async () => {
        setSpFunctionList([]);
        setSpComponentList([]);
        setSpGuaranteeList([]);
        try {
            // * call the API
            const resFunction = await API.get(`/spfunction/getAll`);
            const resComponent = await API.get(`/spcomponent/getAll`);
            const resGuarantees = await API.get(`/benefit/getAllGuarantees`);

            setSpFunctionList(resFunction.data.data);
            setSpComponentList(resComponent.data.data);
            setSpGuaranteeList(resGuarantees.data.data);

        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e);
        }
    }

    // we use useEffect to call the function that fills the grid
    useEffect(() => {
        getBenefits();
        getAllComboList();
    }, []) // eslint-disable-line react-hooks/exhaustive-deps


    /**
     ** Show or hide the modal
     */
    const toggleWindow = () => {
        setShowModal(!showModal);
    }

    /**
     ** Show or hide the alert
     */
    const toggleAlert = () => {
        setShowAlert(!showAlert);
    }

    const onActionClick = (actionItem, dataItem) => {
        const action = actionItem.value;
        setViewType(action);
        setBenefitSelected(dataItem);

        switch (action) {
            case NEW:
                toggleWindow();
                break;
            case VIEW:
                toggleWindow();
                break;
            case UPDATE:
                toggleWindow();
                break;
            case DELETE:
                toggleAlert();
                break;
            default:
                break;
        }
    }

    const onSave = async (data) => {
        try {
            const requestData = { ...data, institutionId: get_currentInstitution().catInstitutionId }

            const error = validate_benefit(requestData, t);



            if (error.length === 0) {

                // * call the api to do the upsert
                const res = await API.post('/benefit/upsert', clean_benefit(requestData));

                // * if errors exist, it is displayed as a notification
                if (res.data.code !== 100) {
                    Notifications.notify(t(`benefit.${res.data.code}`), ERROR_NOTIFICATION);
                    return;
                }

                // * we decide what kind of message of success we should give
                const msg = data.benefitId === 0 ? t('benefit.new_message') : t('benefit.update_message');

                // * hide the modal, shows success message and refresh the grid
                toggleWindow();
                Notifications.notify(msg, SUCCESS_NOTIFICATION);
                getBenefits();
            } else {
                /**Shows error message */
                Notifications.notify(error, ERROR_NOTIFICATION);
            }

        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e);
        }

    }

    const onDelete = async () => {
        try {
            // * call the api to do the delete
            const res = await API.delete(`/benefit?benefitId=${benefitSelected.benefitId}`);

            // * if errors exist, it is displayed as a notification
            if (res.data.code !== 100) {
                Notifications.notify(t(`benefit.${res.data.code}`), ERROR_NOTIFICATION);
                return;
            }

            // * hide the alert and refresh the grid
            toggleAlert();
            Notifications.notify(t('benefit.delete_message'), SUCCESS_NOTIFICATION);
            getBenefits();
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e)
        }
    }

    return (
        <>
            <div className="sub_menu">
                <div className="sub_menu_btns my-1">
                    <button className="btn btn-primary ilo-btn" onClick={() => onActionClick({ value: NEW }, {})}>{t('spComponent.new_btn')}</button> | <span className="ilo-section-title p-1">{t('benefit.modalTitle')}</span>
                </div>
            </div>
            <div className="container-fluid mt-1">
                {/* modal window */}
                {showModal &&
                    <BenefitModal
                        type={viewType}
                        data={benefitSelected}
                        toggleWindow={toggleWindow}
                        onSave={onSave}
                        spComponentList={spComponentList}
                        spFunctionList={spFunctionList}
                        spGuaranteeList={spGuaranteeList}
                    />}

                {/* alert window */}
                {showAlert &&
                    <Dialog
                        className="ilo-delete-confirm"
                        title={t('benefit.delete_title')}
                        onClose={() => toggleAlert()}
                    >
                        <p style={{ margin: "25px", textAlign: "center" }}>{t('benefit.delete_body')}</p>
                        <DialogActionsBar layout="end">
                            <button className="k-button" onClick={onDelete}>{t('alerts.yes_btn')}</button>
                            <button className="k-button" onClick={toggleAlert}>{t('alerts.no_btn')}</button>
                        </DialogActionsBar>
                    </Dialog>
                }

                {/* Grid */}
                <div className="row">
                    {/* grid */}
                    <div className="container_grid">
                        <BenefitGrid data={benefitList} onActionClick={onActionClick} />
                    </div>
                </div>
            </div>
        </>
    )
}

export default Benefit;
